import { DatePipe, isPlatformBrowser, NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { AuthHelpers, IListing, ISite, Listing, PATHS, User } from '@curbnturf/entities';
import { CheckedComponent } from '@curbnturf/form-helpers/src/lib/components/checked/checked.component';
import { listingComplete, siteComplete } from '@curbnturf/helpers';
import { ListingFacade } from '@curbnturf/listing/src/lib/+state/listing.facade';
import { Logger } from '@curbnturf/network';
import { SiteFacade } from '@curbnturf/site';
import { EnableConfirmationModalComponent } from '@curbnturf/site-components/src/lib/enable-confirmation-modal/enable-confirmation-modal.component';
import { SpinnerComponent } from '@curbnturf/ui/src/lib/spinner/spinner.component';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule } from '@ionic/angular';
import { deepEqual } from 'fast-equals';
import { DateTime } from 'luxon';
import { of, Subscription } from 'rxjs';
import { catchError, first, skipWhile } from 'rxjs/operators';
import { BlackoutComponent } from '../blackout/blackout.component';
import { DescriptionComponent } from '../description/description.component';
import { DirectionsComponent } from '../directions/directions.component';
import { EditSitesComponent } from '../edit-sites/edit-sites.component';
import { OverviewComponent } from '../overview/overview.component';
import { PayoutComponent } from '../payout/payout.component';
import { PoliciesComponent } from '../policies/policies.component';

@Component({
  selector: 'curbnturf-listing-edit',
  templateUrl: './listing-edit.component.html',
  styleUrls: ['./listing-edit.component.scss'],
  standalone: true,
  imports: [
    BlackoutComponent,
    CheckedComponent,
    DatePipe,
    DescriptionComponent,
    DirectionsComponent,
    EnableConfirmationModalComponent,
    FormsModule,
    IonicModule,
    NgClass,
    NgFor,
    NgIf,
    OverviewComponent,
    PayoutComponent,
    PoliciesComponent,
    ReactiveFormsModule,
    SpinnerComponent,
    EditSitesComponent,
  ],
})
export class ListingEditComponent implements OnDestroy, OnInit {
  @ViewChild(EnableConfirmationModalComponent, { static: true })
  listingEnableModal: EnableConfirmationModalComponent;

  @ViewChild(PayoutComponent)
  payoutComponent: PayoutComponent;

  @Output() public onListingLoaded: EventEmitter<Listing> = new EventEmitter<Listing>();

  errors: string[] = [];
  listing: Listing;
  previousListing: IListing;

  selectedSiteId: number;
  get selectedSite(): ISite | undefined {
    if (!this.selectedSiteId || !this.listing?.sites?.length) {
      return;
    }

    return this.listing.sites.find((site) => site.id === this.selectedSiteId);
  }

  user: User;

  isNew: boolean;

  section = 'description';
  showedEnableModal = false;

  spinnerOn = false;
  lastSaved: DateTime;

  showMenu: boolean = false;

  get lastSavedTime(): string {
    return this.lastSaved.toLocaleString();
  }

  private subscriptions: Subscription[] = [];
  private listingSelectedSubscription: Subscription;

  constructor(
    private alertFacade: AlertFacade,
    private route: ActivatedRoute,
    private router: Router,
    private listingFacade: ListingFacade,
    private userFacade: UserFacade,
    private siteFacade: SiteFacade,
    private logger: Logger,
    private cd: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  ngOnInit() {
    this.spinnerOn = true;
    if (this.route && this.route.snapshot) {
      if (this.route.snapshot.url && this.route.snapshot.url.length > 0 && this.route.snapshot.url[0].path === 'new') {
        this.isNew = true;
      }
    }

    this.subscriptions.push(
      this.userFacade.currentUserOnce$.subscribe((user) => {
        this.user = user;

        this.subscriptions.push(
          this.route.paramMap.subscribe((params) => {
            const id = parseInt(params.get('id') ?? '', 10);
            if (id) {
              this.listingFacade.select(id);

              if (this.listingSelectedSubscription) {
                this.listingSelectedSubscription.unsubscribe();
              }

              this.listingSelectedSubscription = this.listingFacade.selected$.subscribe({
                next: (listing) => {
                  if (listing && !deepEqual(listing, this.previousListing)) {
                    this.previousListing = new Listing({ ...listing });

                    this.listing = new Listing({
                      ...listing,
                      sites: [...(listing.sites?.map((site) => ({ ...site })) || [])],
                    });

                    if (params.get('site')) {
                      this.selectedSiteId = parseInt(params.get('site') ?? '', 10);
                    }

                    this.selectSite();
                  }
                },
                error: (error) => {
                  this.logger.error('Error Opening Listing For Editing', error);
                  this.alertFacade.display({
                    title: 'Error Opening Listing',
                    body: 'An error occurred and we were unable to open the listing for editing.',
                    level: 'error',
                  });
                },
                complete: () => {
                  this.spinnerOn = false;
                },
              });

              this.subscriptions.push(this.listingSelectedSubscription);
            } else {
              this.alertFacade.display({
                title: 'Listing ID Not Supplied',
                body: 'Unable to open listing without an identifier.',
                level: 'error',
              });
              this.spinnerOn = false;
            }

            if (params.get('section')) {
              this.section = params.get('section') || 'message';
            }
          }),

          this.listingFacade.error$.subscribe((error: string) => {
            if (error) {
              this.spinnerOn = false;
              this.errors.push(error);
            }
          }),

          this.listingFacade.selected$
            .pipe(
              skipWhile((listing) => !listing),
              catchError(() => of()),
            )
            .subscribe(async (listing: Listing) => {
              this.spinnerOn = false;

              if (!listing) {
                return;
              }

              if (
                !listing.user ||
                (!(listing.user?.id === this.user?.id) &&
                  !AuthHelpers.hasSalesOwnership(this.user, listing?.user) &&
                  !AuthHelpers.isAdminRole(this.user))
              ) {
                await this.router.navigateByUrl(PATHS.properties);
              }

              if (listing.id && this.isNew) {
                await this.router.navigate(['edit', listing.id], {
                  relativeTo: this.route.parent,
                });
              }

              // @todo fix this method
              const listingsReadySubscription: Subscription = this.listingFacade.listingsReadyToEnable$.subscribe(
                (listings) => {
                  if (
                    listings.length > 0 &&
                    !this.showedEnableModal &&
                    listings.find((listing) => listing?.id === this.listing?.id)
                  ) {
                    this.showedEnableModal = true;
                    this.listingEnableModal.openModal();
                  }

                  setTimeout(
                    () =>
                      this.showedEnableModal &&
                      listingsReadySubscription &&
                      // if modal showed then we unsubscribe
                      listingsReadySubscription.unsubscribe(),
                  );
                },
              );

              this.subscriptions.push(listingsReadySubscription);
            }),
        );
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  onSiteChanged(navigationData: { siteId: number; section?: string }) {
    if (navigationData?.section) {
      this.selectNavigation(navigationData.section, navigationData.siteId);
    }
  }

  async selectNavigation(navigationString: string, siteId?: number) {
    // Close the menu
    this.showMenu = false;

    if (this.section === navigationString) {
      return;
    }

    // scroll to top on navigation
    if (isPlatformBrowser(this.platformId) && window) {
      window.scrollTo(0, 0);
    }

    this.section = navigationString;

    if (this.listing?.id) {
      let options: { section: string; site?: number } = { section: navigationString };
      if (siteId) {
        options.site = siteId;

        if (siteId !== this.selectedSiteId) {
          const site = this.listing.sites?.find((listingSite) => listingSite.id === siteId);
          this.selectSite(site);
        }
      } else if (this.selectedSite) {
        options.site = this.selectedSite?.id;
      }

      await this.router.navigate(['edit', this.listing.id, options], {
        relativeTo: this.route.parent,
      });
    }
  }

  saved(saved: boolean) {
    if (saved) {
      this.lastSaved = DateTime.now();
    }
  }

  onShowMenu() {
    this.showMenu = true;
  }

  onHideMenu() {
    this.showMenu = false;
  }

  onToggleMenu() {
    if (this.showMenu) {
      this.onHideMenu();
    } else {
      this.onShowMenu();
    }
  }

  overviewCompleted(): boolean {
    if (this.selectedSite) {
      return siteComplete.overviewCompleted(this.selectedSite);
    }

    return false;
  }

  sitesCompleted(): boolean {
    return listingComplete.sitesCompleted(this.listing);
  }

  priceCompleted(): boolean {
    if (this.selectedSite) {
      return siteComplete.priceCompleted(this.selectedSite);
    }

    return false;
  }

  descriptionCompleted(): boolean {
    return listingComplete.descriptionCompleted(this.listing);
  }

  directionsCompleted(): boolean {
    return listingComplete.directionsCompleted(this.listing);
  }

  bookingCompleted(): boolean {
    return listingComplete.bookingCompleted(this.listing);
  }

  onSelectSite(event: CustomEvent) {
    const site: ISite = event.detail.value;
    if (site?.id) {
      this.siteFacade.selectSite(site);
      this.selectedSiteId = site.id;

      const params = this.router.url.split(';') || [];
      const route = params.shift()?.split('/') || [];

      this.router.navigate(
        [...route, { section: params.find((param) => param.startsWith('section'))?.split('=')?.[1], site: site.id }],
        {
          replaceUrl: true,
        },
      );
    }
  }

  /**
   * Select a site to edit.
   * @private
   */
  private selectSite(site?: ISite) {
    if (this.listing.sites && Array.isArray(this.listing.sites) && this.listing.sites.length > 0) {
      if (!site?.id) {
        // If no site is passed, we need to find one to select.
        if (this.selectedSiteId) {
          // If there is a selected site ID, we will try to find it in the list.
          site = this.listing.sites?.slice().find((el) => el.id === this.selectedSiteId);
        }

        // If no site is found, we will select the first one in the list.
        if (!site) {
          site = this.listing.sites?.slice().shift();
        }
      }

      if (site?.id) {
        this.selectedSiteId = site.id;
        this.subscriptions.push(
          this.siteFacade.selected$.pipe(first()).subscribe((selectedSite) => {
            if (site && (!selectedSite || selectedSite?.id !== site?.id)) {
              this.siteFacade.selectSite(site);
              this.cd.detectChanges();
            }
          }),
        );
      }
    }
  }

  protected readonly siteComplete = siteComplete;

  getSiteName(site: ISite) {
    if (site.name) {
      return site.name;
    } else {
      return 'Your New Site';
    }
  }
}
